import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Store, 
  Camera, 
  Tag, 
  Upload, 
  ShieldCheck, 
  Truck, 
  CheckCircle, 
  AlertCircle,
  ArrowRight,
  Users,
  MessageSquare,
  TrendingUp,
  Clock,
  Award,
  BarChart,
  HelpCircle,
  Leaf
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';
import MobileBottomNav from '../components/layout/MobileBottomNav';

const HowToSell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [activeStep, setActiveStep] = useState(null);
  const [activeTab, setActiveTab] = useState('steps'); // steps, tips, requirements

  const brandColor = '#ff660e';
  const brandColorLight = '#fff0e6';

  const sellingSteps = [
    {
      id: 1,
      title: "Setup Your Store",
      description: "Create and customize your EcoMart store with basic information",
      icon: <Store className="w-6 h-6" />,
      details: [
        "Go to 'My Store' in your profile",
        "Fill in store name, description, and logo",
        "Add contact information and business hours",
        "Set up your store policies and return rules"
      ],
      tips: ["Use a professional store name", "Be clear about your niche"],
      note: "Your store will be pending moderator approval before going live",
      iconColor: "text-blue-500",
      bgColor: "bg-blue-50",
      action: { label: "Setup Store", path: "/store" }
    },
    {
      id: 2,
      title: "Take Quality Photos",
      description: "Capture clear, attractive photos of your products or services",
      icon: <Camera className="w-6 h-6" />,
      details: [
        "Use good lighting (natural light is best)",
        "Take multiple angles of each product",
        "Show any defects or unique features",
        "Use a clean, uncluttered background"
      ],
      tips: ["Use a smartphone with good camera", "Edit photos for brightness/contrast"],
      iconColor: "text-purple-500",
      bgColor: "bg-purple-50",
      action: { label: "View Photo Guidelines" }
    },
    {
      id: 3,
      title: "Set Competitive Prices",
      description: "Research and set attractive prices for your listings",
      icon: <Tag className="w-6 h-6" />,
      details: [
        "Check similar products on EcoMart",
        "Consider your costs and desired profit",
        "Offer bundle discounts for multiple items",
        "Be open to reasonable negotiations"
      ],
      tips: ["Start slightly higher for negotiation room", "Offer free delivery as incentive"],
      iconColor: "text-green-500",
      bgColor: "bg-green-50",
      action: { label: "Pricing Calculator" }
    },
    {
      id: 4,
      title: "Create Your Listings",
      description: "List your products or services for FREE on EcoMart",
      icon: <Upload className="w-6 h-6" />,
      details: [
        "Click 'Sell' button on any page",
        "Fill in detailed product description",
        "Upload multiple high-quality photos",
        "Select appropriate categories and tags"
      ],
      tips: ["Be honest about condition", "Include dimensions and specifications"],
      note: "Listings await moderator approval before going public",
      iconColor: "text-yellow-500",
      bgColor: "bg-yellow-50",
      action: { label: "Start Selling", path: "/store" }
    },
    {
        id: 5,
        title: "Boost Your Listings",
        description: "Increase visibility, attract more buyers, and sell faster",
        icon: <TrendingUp className="w-6 h-6" />,
        details: [
          "Your listings appear at the top of search results",
          "Reach more buyers actively searching for similar items",
          "Get more views, messages, and profile visits",
          "Stand out from competing listings in the same category",
          "Sell items faster without reducing confirmation or trust"
        ],
        tips: [
          "Use clear titles and relevant keywords",
        ],
        iconColor: "text-pink-500",
        bgColor: "bg-pink-50"
      },      
    {
      id: 6,
      title: "Communicate with Buyers",
      description: "Respond to inquiries and negotiate through our in-app chat",
      icon: <MessageSquare className="w-6 h-6" />,
      details: [
        "Respond promptly to buyer messages",
        "Be professional and courteous",
        "Provide additional information when asked",
        "Arrange public meeting locations"
      ],
      tips: ["Set up quick responses", "Be transparent about availability"],
      iconColor: "text-teal-500",
      bgColor: "bg-teal-50"
    },
    {
      id: 7,
      title: "Secure Payment Process",
      description: "Use our escrow system - never ask for direct payments",
      icon: <ShieldCheck className="w-6 h-6" />,
      details: [
        "Buyer makes payment to EcoMart escrow",
        "Funds are held securely until delivery",
        "Only accept payment through our platform",
        "Never share personal banking details"
      ],
      warning: "⚠️ Never ask buyers to pay directly to your personal account",
      iconColor: "text-red-500",
      bgColor: "bg-red-50",
      action: { label: "Escrow Guide", path: "/escrow" }
    },
    {
      id: 8,
      title: "Deliver as Promised",
      description: "Ensure product matches listing and deliver on time",
      icon: <Truck className="w-6 h-6" />,
      details: [
        "Pack items securely for delivery",
        "Meet at agreed public location",
        "Bring all accessories and documentation",
        "Allow buyer to inspect the product"
      ],
      tips: ["Take delivery confirmation photos", "Get signature for high-value items"],
      iconColor: "text-indigo-500",
      bgColor: "bg-indigo-50"
    },
    {
      id: 9,
      title: "Release & Dispute Handling",
      description: "Handle funds release and know when to raise disputes",
      icon: <CheckCircle className="w-6 h-6" />,
      details: [
        "Buyer releases funds after satisfaction",
        "Funds are transferred to your account",
        "If buyer doesn't release funds, raise dispute",
        "Provide evidence for dispute resolution"
      ],
      warning: "⚠️ Raise dispute within 48 hours if buyer doesn't release funds after delivery",
      iconColor: "text-emerald-500",
      bgColor: "bg-emerald-50",
      action: { label: "Raise Dispute", path: "/chats" }
    }
  ];

  const sellerTips = [
    {
      title: "Build Your Reputation",
      content: "Maintain high ratings by delivering quality products and excellent service"
    },
    {
      title: "Quick Responses",
      content: "Respond to buyer inquiries within 2 hours to increase sales"
    },
    {
      title: "Seasonal Listings",
      content: "List seasonal items 2-3 weeks before peak demand"
    },
    {
      title: "Package Deals",
      content: "Offer bundles of related products to increase average order value"
    },
    {
      title: "Regular Updates",
      content: "Refresh your store with new products at least once a week"
    }
  ];

  const storeRequirements = [
    {
      title: "Store Information",
      items: ["Valid store name", "Clear description", "Contact information", "Business hours"]
    },
    {
      title: "Product Listings",
      items: ["Clear photos", "Accurate descriptions", "Honest condition", "Reasonable pricing"]
    },
    {
      title: "Compliance",
      items: ["No prohibited items", "Clear return policy", "Authentic products", "Legal to sell"]
    },
    {
      title: "Seller Account",
      items: ["Verified identity", "Bank account setup", "Tax information", "Active status"]
    }
  ];

  const handleStepClick = (stepId) => {
    setActiveStep(activeStep === stepId ? null : stepId);
  };

  const handleStartSelling = () => {
    if (user) {
      navigate('/store');
    } else {
      navigate('/login', { state: { redirectTo: '/store' } });
    }
  };

  const handleViewStore = () => {
    if (user) {
      navigate('/store');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header with EcoMart Branding */}
        <div className="mb-10 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6"
               style={{ backgroundColor: brandColorLight }}>
            <div className="flex items-center">
              <Store className="w-8 h-8 ml-1" style={{ color: brandColor }} />
            </div>
          </div>
          <div className="flex items-center justify-center mb-3">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
              How to Sell on
            </h1>
            <span className="ml-2 text-3xl md:text-4xl font-bold" style={{ color: brandColor }}>
              EcoMart
            </span>
          </div>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Turn your products or services into profit with our secure and sustainable marketplace
          </p>
        </div>


        {/* Important Notice */}
        <div className="rounded-2xl p-6 mb-10 border" 
             style={{ 
               backgroundColor: brandColorLight,
               borderColor: brandColor + '30'
             }}>
          <div className="flex flex-col md:flex-row items-start md:items-center">
            <div className="flex items-start mb-4 md:mb-0 md:mr-6">
              <AlertCircle className="w-6 h-6 mr-3 flex-shrink-0" style={{ color: brandColor }} />
              <div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: brandColor }}>
                  Important: Escrow Protection
                </h3>
                <p className="text-gray-800">
                  Always use EcoMart's escrow system for payments. Direct payments to personal accounts 
                  are prohibited and not protected by our buyer protection policies.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/escrow')}
              className="px-6 py-3 rounded-lg font-semibold transition-all hover:shadow-md md:ml-auto whitespace-nowrap"
              style={{ 
                backgroundColor: brandColor,
                color: 'white'
              }}
            >
              Learn About Escrow
            </button>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-2xl shadow-sm p-2 mb-8">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveTab('steps')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'steps'
                  ? 'text-white'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              style={{ 
                backgroundColor: activeTab === 'steps' ? brandColor : 'transparent'
              }}
            >
              Selling Steps
            </button>
            <button
              onClick={() => setActiveTab('tips')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'tips'
                  ? 'text-white'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              style={{ 
                backgroundColor: activeTab === 'tips' ? brandColor : 'transparent'
              }}
            >
              Pro Tips
            </button>
            <button
              onClick={() => setActiveTab('requirements')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'requirements'
                  ? 'text-white'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              style={{ 
                backgroundColor: activeTab === 'requirements' ? brandColor : 'transparent'
              }}
            >
              Requirements
            </button>
          </div>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'steps' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Steps List */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Complete Selling Process</h2>
                
                <div className="space-y-4">
                  {sellingSteps.map((step) => (
                    <div
                      key={step.id}
                      className={`border rounded-xl transition-all hover:shadow-md cursor-pointer overflow-hidden ${
                        activeStep === step.id ? 'border-orange-300' : 'border-gray-200'
                      }`}
                      style={{ 
                        borderLeftColor: activeStep === step.id ? brandColor : '',
                        borderLeftWidth: activeStep === step.id ? '4px' : ''
                      }}
                      onClick={() => handleStepClick(step.id)}
                    >
                      <div className="p-5">
                        <div className="flex items-start">
                          <div className={`p-3 rounded-lg ${step.bgColor} mr-4 flex-shrink-0`}>
                            <div className={step.iconColor}>{step.icon}</div>
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div className="min-w-0">
                                <div className="flex items-center mb-2">
                                  <span className="inline-flex items-center justify-center w-8 h-8 text-white rounded-full text-sm font-bold mr-3 flex-shrink-0"
                                        style={{ backgroundColor: brandColor }}>
                                    {step.id}
                                  </span>
                                  <h3 className="text-lg font-semibold text-gray-900">
                                    {step.title}
                                  </h3>
                                </div>
                                <p className="text-gray-600 mb-3">{step.description}</p>
                              </div>
                              
                              <button className="text-gray-400 hover:text-orange-500 ml-4 flex-shrink-0">
                                <ArrowRight className={`w-5 h-5 transition-transform ${
                                  activeStep === step.id ? 'rotate-90' : ''
                                }`} />
                              </button>
                            </div>

                            {/* Expanded Details */}
                            {activeStep === step.id && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <h4 className="font-medium text-gray-900 mb-3">Detailed Instructions:</h4>
                                <ul className="space-y-2 mb-4">
                                  {step.details.map((detail, index) => (
                                    <li key={index} className="flex items-start">
                                      <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                      <span className="text-gray-700">{detail}</span>
                                    </li>
                                  ))}
                                </ul>
                                
                                {step.tips && (
                                  <div className="bg-blue-50 rounded-lg p-4 mb-4">
                                    <h5 className="font-medium text-blue-800 mb-2">💡 Pro Tips:</h5>
                                    <ul className="text-blue-700 space-y-1">
                                      {step.tips.map((tip, index) => (
                                        <li key={index}>{tip}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                
                                {step.note && (
                                  <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4 mb-4">
                                    <h5 className="font-medium text-yellow-800 mb-1">📝 Note:</h5>
                                    <p className="text-yellow-700">{step.note}</p>
                                  </div>
                                )}
                                
                                {step.warning && (
                                  <div className="bg-red-50 border border-red-100 rounded-lg p-4 mb-4">
                                    <h5 className="font-medium text-red-800 mb-1">⚠️ Important:</h5>
                                    <p className="text-red-700">{step.warning}</p>
                                  </div>
                                )}
                                
                                {step.action && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(step.action.path);
                                    }}
                                    className="px-4 py-2 rounded-lg font-medium transition-all hover:shadow-sm"
                                    style={{ 
                                      backgroundColor: brandColorLight,
                                      color: brandColor
                                    }}
                                  >
                                    {step.action.label} →
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Start */}
              <div className="rounded-2xl p-4 md:p-6 text-white overflow-hidden relative"
                 style={{ backgroundColor: brandColor }}>
                <Clock className="w-12 h-12 mb-4 opacity-90" />
                <h3 className="text-xl font-bold mb-3">Quick Start Guide</h3>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <span className="inline-block w-6 h-6 bg-white text-orange-600 rounded-full text-center text-sm font-bold mr-3 flex-shrink-0">
                      1
                    </span>
                    <span>Setup your store profile</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-block w-6 h-6 bg-white text-orange-600 rounded-full text-center text-sm font-bold mr-3 flex-shrink-0">
                      2
                    </span>
                    <span>Take quality photos</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-block w-6 h-6 bg-white text-orange-600 rounded-full text-center text-sm font-bold mr-3 flex-shrink-0">
                      3
                    </span>
                    <span>List first product</span>
                  </li>
                </ul>
                <button
                  onClick={handleStartSelling}
                  className="w-full bg-white text-orange-600 font-semibold py-3 rounded-lg hover:bg-emerald-50 transition-colors"
                >
                  Start Selling Now
                </button>
              </div>

              {/* Support Card */}
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Seller Support</h3>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/support')}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <span className="font-medium text-gray-900">Seller Help Center</span>
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tips' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              {sellerTips.map((tip, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-sm p-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mr-4"
                         style={{ backgroundColor: brandColorLight }}>
                      <span className="font-bold" style={{ color: brandColor }}>{index + 1}</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{tip.title}</h3>
                      <p className="text-gray-600">{tip.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'requirements' && (
          <div className="space-y-8">
            {/* Store Requirements */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Store Setup Requirements</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {storeRequirements.map((requirement, index) => (
                  <div key={index} className="border border-gray-200 rounded-xl p-5">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4"
                         style={{ backgroundColor: brandColorLight }}>
                      <span className="font-bold" style={{ color: brandColor }}>{index + 1}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      {requirement.title}
                    </h3>
                    <ul className="space-y-2">
                      {requirement.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-center text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Approval Process */}
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-2xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Moderator Approval Process</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-5">
                  <div className="text-3xl font-bold mb-2" style={{ color: brandColor }}>1-2 Hours</div>
                  <p className="text-gray-600">Store approval time</p>
                </div>
                <div className="bg-white rounded-xl p-5">
                  <div className="text-3xl font-bold mb-2" style={{ color: brandColor }}>1-2 Hours</div>
                  <p className="text-gray-600">Listing approval time</p>
                </div>
                <div className="bg-white rounded-xl p-5">
                  <div className="text-3xl font-bold mb-2" style={{ color: brandColor }}>100%</div>
                  <p className="text-gray-600">Manual review of all submissions</p>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="text-center">
              <div className="inline-flex flex-col items-center p-8 rounded-2xl max-w-2xl mx-auto"
                   style={{ backgroundColor: brandColorLight }}>
                <Store className="w-16 h-16 mb-4" style={{ color: brandColor }} />
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Ready to Start Selling?</h2>
                <p className="text-gray-600 mb-6 max-w-md">
                  Join thousands of successful sellers on EcoMart. Start for free and grow your business sustainably.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={handleStartSelling}
                    className="px-8 py-3 rounded-lg font-semibold transition-all hover:shadow-lg"
                    style={{ 
                      backgroundColor: brandColor,
                      color: 'white'
                    }}
                  >
                    Create Your Store
                  </button>
                  
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default HowToSell;